









































































































































































































import { computed, defineComponent, onMounted, ref, watch, ssrRef, useAsync, useContext } from '@nuxtjs/composition-api'
import useBreakpoint from '@/composables/use-breakpoint'
import useSearchBar from '@/composables/use-search-bar'
import useStore from '@/composables/use-store'
import Store from '@/classes/Store'
import { parseSearchUrlQuery as parseQuery } from '@/modules/route-query'
import PricedCar from '@/classes/PricedCar'
import useCancelTerms from '@/composables/use-cancel-terms'
import { AutoRental, WithContext } from 'schema-dts'
import { useEventListener } from '@vueuse/core'
import useI18n from '@/composables/use-i18n'
import ggoApi from '@/modules/ggo-api'
import GBtn from '@/components/GBtn.vue'
import StoreSearchCard from '@/components/store/StoreSearchCard.vue'
import DateHourRange from '@/classes/DateHourRange'
import useDefaultMeta from '@/composables/use-default-meta'
import { ORGANIZATION_JSONLD } from '@/assets/constants/common-jsonld'
import useComment from '@/composables/use-comment'
import useScrollTo from '@/composables/use-scroll-to'
import { tdCarStoreReview, tdCarStoreSearch } from '@/modules/ga4'
import useSeats, { SeatsValue } from '@/composables/use-seats'
import { FeatureFlagKey } from '@/modules/growthbook/feature-flag'

const { stringify } = JSON

export default defineComponent({
  layout: 'store',
  name: 'Store',
  head: {},
  watchQuery: ['id'],
  jsonld () {
    const store = (this.store as Store)
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'AutoRental',
        name: store.name,
        legalName: store.taxRegistrationName,
        address: store.address,
        geo: {
          '@type': 'GeoCoordinates',
          latitude: store.coordinate.lat,
          longitude: store.coordinate.lng,
        },
        url: store.fullStoreLink,
        image: store.img,
        review: {
          '@type': 'Review',
          itemReviewed: {
            '@type': 'AutoRental',
            name: store.name,
            address: store.address,
          },
          reviewRating: {
            '@type': 'AggregateRating',
            ratingValue: store.rating?.score || 5,
            bestRating: store.score?.all || 5,
            ratingCount: store.rating?.count || 1,
            reviewCount: store.rating?.count || 1,
          },
          author: ORGANIZATION_JSONLD,
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: store.rating?.score || 5,
          bestRating: store.score?.all || 5,
          ratingCount: store.rating?.count || 1,
          reviewCount: store.rating?.count || 1,
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: store.operationTime?.weekday[0],
            closes: store.operationTime?.weekday[1],
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Saturday', 'Sunday'],
            opens: store.operationTime?.weekend[0],
            closes: store.operationTime?.weekend[1],
          },
        ],
        slogan: store.announce,
      } as WithContext<AutoRental>,
    ]
  },
  async asyncData ({ error, params }) {
    if (!params.id) throw new Error('Missing `params.id`!')
    const { data, error: err } = await ggoApi.getStore({ id: Number(params.id) })

    if (!data || err) {
      error({ statusCode: 404 })
    }
  },
  setup () {
    const { params, query } = useContext()
    const i18n = useI18n()

    /**
     * url裡有帶時間區段就不跳popup, 否則跳
     */
    const showSearchPopup = ref(!parseQuery(query.value).dateHourRange)
    const closeSearchPopup = () => {
      showSearchPopup.value = false
    }

    // Mobile search bar 可以觸發 click outside
    const searchCardActivator = ref<InstanceType<typeof GBtn> | null>(null)
    const searchCard = ref<InstanceType<typeof StoreSearchCard> | null>(null)

    /** 處理點擊選單外事件 */
    const handler = ({ target }: Event) => {
      /**
       * workaround: Mobile 點擊 date picker body 後會縮起來
       */
      if (mobileSearchCardOpen.value) {
        /**
         * Safari workaround: contains 不正常 work
         * 同時 closest 的效能也比 contains 好
         * 因此使用 closest
         */
        if ((target as HTMLElement)?.closest('#date-picker__body')) return

        if ([searchCard.value?.$el, searchCardActivator.value?.$el].every(
          el => !(el === target || el?.contains(target as HTMLElement))
        )) {
          closeMobileSearchCard()
        }
      }
    }
    onMounted(() => useEventListener(document.body, 'click', handler))

    const { fetchStore, fetchStoreCars } = useStore()

    const store = ssrRef<Store | null>(null)
    const fetchAsyncData = async () => {
      store.value = await fetchStore(+params.value.id)
    }
    useAsync(fetchAsyncData)
    const carList = ref<PricedCar[]>([])

    const { match } = useBreakpoint()
    const isMobile = !match('lg')

    const googleMapLink = computed(() => store.value?.GoogleMapLink ?? '')

    // Search related
    const mobileSearchCardOpen = ref(false)
    const handleEditClick = () => { mobileSearchCardOpen.value = !mobileSearchCardOpen.value }
    const closeMobileSearchCard = () => {
      mobileSearchCardOpen.value = false
    }

    const { dateHourRange, seats, pushQuery, queryObject, setValueFromQuery } = useSearchBar({
      pushQuery: {
        pushSearchPage: false,
        pushCountry: false,
      },
    })

    const rangeTexts = computed((): [string, string] => {
      if (!dateHourRange.value) return Array(2).fill('') as [string, string]
      return [dateHourRange.value.start.dateHourString, dateHourRange.value.end.dateHourString]
    })

    const { getName } = useSeats()
    const submit = () => {
      closeSearchPopup()
      closeMobileSearchCard()

      pushQuery(queryObject.value)

      tdCarStoreSearch({
        pick_date: dateHourRange.value.start.dateHourString,
        return_date: dateHourRange.value.end.dateHourString,
        number: getName(seats.value as SeatsValue),
      }, isMobile)

      scrollToSearchedCarList()
    }

    watch(store, (val) => {
      if (val) {
        const defaultStoreDateHourRange = DateHourRange.fromStore(val)
        if (defaultStoreDateHourRange) {
          // push query 用的 dateHourRange
          let _dateHourRange = dateHourRange.value.clone()

          if (_dateHourRange.isDateEmpty) {
            _dateHourRange = defaultStoreDateHourRange
          } else {
            if (_dateHourRange.isStartExpired()) {
              _dateHourRange.setStart(defaultStoreDateHourRange.start)
            }
            if (_dateHourRange.isEndExpired()) {
              _dateHourRange.setEnd(defaultStoreDateHourRange.end)
            }
            if (!_dateHourRange.isValid) _dateHourRange = defaultStoreDateHourRange
          }

          pushQuery({
            ..._dateHourRange.queryObject,
          })
        }
      }
    })

    onMounted(() => {
      // 網址參數 → 表單值
      watch(() => stringify(parseQuery(query.value)), async () => {
        setValueFromQuery(query)

        // 有日期才 call api
        if (query.value.startDate && query.value.endDate) { carList.value = await fetchStoreCars(+params.value.id, queryObject.value) }
      }, { immediate: true })
    })

    const overDistanceRule = computed(() => String(i18n.t('colon', [i18n.t('store.title.overDistanceRule'), store.value?.overDistanceRule])))
    const fuelRule = computed(() => String(i18n.t('colon', [i18n.t('store.title.fuelRule'), store.value?.fuelRule])))
    const cancelTerm = computed(() => String(i18n.t('colon', [i18n.t('store.title.cancelRule'), store.value?.cancelRule
      ? useCancelTerms(store.value.cancelRule)?.content
      : ''])))

    const city = computed(() => store.value?.city)

    useDefaultMeta({
      title: computed(() => String(i18n.t('SEO.store.title', [city.value, store.value?.name]))),
      description: computed(() => String(i18n.t('SEO.store.metaDescription', [city.value, store.value?.name]))),
    })

    const bannerAlt = computed(() => String(i18n.t('SEO.store.img.banner', [city.value, store.value?.name])))

    // 右邊那個 searchCard
    const datePickerMenuOptions = {
      placement: 'bottom-end',
      strategy: 'absolute',
      modifiers: [{ name: 'offset', options: { offset: [-270, 20] } }],
    }

    const searchedCarList = ref<HTMLElement>()
    const { scrollTo: scrollToSearchedCarList } = useScrollTo(searchedCarList, { offset: -200 })

    // Comments
    const { setStoreId, getStoreComments, comments, isAllLoaded } = useComment()

    const fetchAsyncComments = async () => {
      setStoreId(+params.value.id)
      await getStoreComments()
    }
    useAsync(fetchAsyncComments)

    const onCommentLinkClick = () => {
      tdCarStoreReview(store.value?.name ?? '')
    }

    return {
      searchCardActivator,
      searchCard,
      isMobile,

      store,
      googleMapLink,

      mobileSearchCardOpen,
      handleEditClick,

      dateHourRange,
      seats,

      carList,
      rangeTexts,

      submit,

      overDistanceRule,
      fuelRule,
      cancelTerm,

      showSearchPopup,
      closeSearchPopup,

      bannerAlt,

      datePickerMenuOptions,

      searchedCarList,

      isAllLoaded,
      comments,

      onCommentLinkClick,

      FeatureFlagKey,
    }
  },
})
